import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../../services/AuthService';
import logo from '../../../assets/logo.png';

const ACCENT = '#005e5d';
const BORDER = '#E6E7EB';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';
const DESTRUCTIVE = '#D32F2F';

/**
 * Reset Password Page
 * Allows user to set their own password after initial login with temporary password
 */
export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!password.trim()) {
      setError('Vui lòng nhập mật khẩu mới');
      return;
    }

    if (password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    if (password !== confirmPassword) {
      setError('Mật khẩu không khớp');
      return;
    }

    if (password === confirmPassword && password.length === 0) {
      setError('Vui lòng nhập mật khẩu');
      return;
    }

    try {
      setLoading(true);
      const response = await AuthService.resetPassword({ newPassword: password });

      if (response.statusCode === 200) {
        setSuccess('Đặt lại mật khẩu thành công! Đang chuyển hướng...');
        setTimeout(() => {
          navigate('/admin', { replace: true });
        }, 1500);
      } else {
        setError(response.error || 'Đặt lại mật khẩu thất bại');
      }
    } catch (err) {
      setError('Lỗi kết nối. Vui lòng thử lại');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F2F3F5',
        backgroundImage: `
          linear-gradient(135deg, rgba(0, 94, 93, 0.05) 0%, rgba(1, 126, 110, 0.05) 100%)
        `,
        padding: '20px',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: '16px',
            border: `1px solid ${BORDER}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: 72,
                height: 72,
                borderRadius: '14px',
                objectFit: 'contain',
                mx: 'auto',
                mb: 2,
                display: 'block',
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: TEXT_PRIMARY,
                mb: 0.5,
                letterSpacing: '-0.01em',
              }}
            >
              Đặt Lại Mật Khẩu
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: TEXT_SECONDARY,
              }}
            >
              Vui lòng tạo một mật khẩu mới cho tài khoản của bạn
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                borderRadius: '8px',
                backgroundColor: '#FFEBEE',
                color: DESTRUCTIVE,
                '& .MuiAlert-icon': {
                  color: DESTRUCTIVE,
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 2.5,
                borderRadius: '8px',
              }}
            >
              {success}
            </Alert>
          )}

          {/* Reset Password Form */}
          <Box component="form" onSubmit={handleResetPassword} noValidate>
            {/* New Password Field */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 0.75, fontSize: '13px' }}
              >
                Mật Khẩu Mới
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="Nhập mật khẩu mới"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: TEXT_TERTIARY, opacity: 0.7 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: TEXT_TERTIARY, mr: 1 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={loading}
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ color: TEXT_TERTIARY }} />
                        ) : (
                          <VisibilityIcon sx={{ color: TEXT_TERTIARY }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Confirm Password Field */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 0.75, fontSize: '13px' }}
              >
                Xác Nhận Mật Khẩu
              </Typography>
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Xác nhận mật khẩu mới"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: TEXT_TERTIARY, opacity: 0.7 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: TEXT_TERTIARY, mr: 1 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleToggleConfirmPasswordVisibility}
                        edge="end"
                        disabled={loading}
                        size="small"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon sx={{ color: TEXT_TERTIARY }} />
                        ) : (
                          <VisibilityIcon sx={{ color: TEXT_TERTIARY }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              type="submit"
              disabled={loading}
              sx={{
                height: '44px',
                borderRadius: '10px',
                backgroundColor: ACCENT,
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '15px',
                textTransform: 'none',
                transition: 'all 0.3s ease',
                mb: 2,
                '&:hover:not(:disabled)': {
                  backgroundColor: '#004d4c',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 16px ${ACCENT}30`,
                },
                '&:active:not(:disabled)': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  backgroundColor: BORDER,
                  color: TEXT_TERTIARY,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : (
                'Đặt Lại Mật Khẩu'
              )}
            </Button>

            {/* Info Text */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: TEXT_TERTIARY,
                fontStyle: 'italic',
              }}
            >
              Mật khẩu phải có ít nhất 6 ký tự
            </Typography>
          </Box>
        </Paper>

        {/* Footer Info */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" sx={{ color: TEXT_TERTIARY }}>
            Phiên bản 1.0 | © 2026 AnhNguJennifer Admin
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;
